"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/primitives/card";
import { Badge } from "@/ui/components/primitives/badge";
import { TrendingUp, DollarSign, CreditCard, AlertCircle } from "lucide-react";
import type { FinancialOversight } from "@/services/api/services/admin-dashboard";
import { formatCurrency, transformRevenueTrends, safeNumber } from "@/services/api/services/admin-dashboard";

interface FinancialOversightEnhancedProps {
  data: FinancialOversight;
}

export function FinancialOversightEnhanced({ data }: FinancialOversightEnhancedProps) {
  // Use the transform function for consistency
  const transformedTrends = transformRevenueTrends(data.platformRevenue.revenueTrends);

  // Calculate monthly growth with null safety
  const trends = data.platformRevenue.revenueTrends;
  const monthlyGrowth = trends.length >= 2
    ? ((safeNumber(trends[1]?.revenue) - safeNumber(trends[0]?.revenue)) /
       Math.max(safeNumber(trends[0]?.revenue), 1) * 100)
    : 0;

  return (
    <div className="admin-grid admin-grid-3">
      {/* Revenue Overview */}
      <Card className="admin-card-plain">
        <CardHeader className="admin-card-header">
          <CardTitle className="admin-card-title flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Revenue Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="admin-card-body">
          <div className="space-y-4">
            <div>
              <div className="admin-text-xs admin-text-muted">Daily Revenue</div>
              <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600 }}>
                {formatCurrency(safeNumber(data.platformRevenue.dailyRevenue))}
              </div>
            </div>

            <div>
              <div className="admin-text-xs admin-text-muted">Monthly Revenue</div>
              <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600 }}>
                {formatCurrency(safeNumber(data.platformRevenue.monthlyRevenue))}
              </div>
              {monthlyGrowth !== 0 && (
                <div className="admin-text-xs admin-text-muted" style={{ marginTop: '2px' }}>
                  {monthlyGrowth > 0 ? '+' : ''}{monthlyGrowth.toFixed(1)}% from last month
                </div>
              )}
            </div>

            <div style={{ height: '1px', background: 'var(--border)', margin: '8px 0' }} />

            <div className="space-y-2">
              {data.platformRevenue.revenueTrends.slice(-3).reverse().map((trend, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="admin-text-xs admin-text-muted">
                    {new Date(trend.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                  </span>
                  <span className="admin-text-sm" style={{ fontWeight: 500 }}>
                    {formatCurrency(safeNumber(trend.revenue))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payout Operations */}
      <Card className="admin-card-plain">
        <CardHeader className="admin-card-header">
          <CardTitle className="admin-card-title flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Payout Operations
          </CardTitle>
        </CardHeader>
        <CardContent className="admin-card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div>
              <div className="admin-text-xs admin-text-muted">Pending approvals</div>
              <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600 }}>
                {safeNumber(data.payoutOperations.pendingApprovals)}
              </div>
            </div>
            <Badge className={data.payoutOperations.pendingApprovals > 0 ? "admin-badge-warning" : "admin-badge-success"}>
              {data.payoutOperations.pendingApprovals > 0 ? 'Action needed' : 'Queue clear'}
            </Badge>
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px', marginBottom: '8px' }}>
            <div>
              <div className="admin-text-xs admin-text-muted">Approved today</div>
              <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600 }}>
                {safeNumber(data.payoutOperations.approvedToday)}
              </div>
            </div>
            <div>
              <div className="admin-text-xs admin-text-muted">Rejected today</div>
              <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600, color: 'var(--admin-danger)' }}>
                {safeNumber(data.payoutOperations.rejectedToday)}
              </div>
            </div>
          </div>

          <div style={{ height: '1px', background: 'var(--admin-border)', margin: '6px 0' }} />

          <div>
            <div className="admin-text-xs admin-text-muted">Total payout volume</div>
            <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600 }}>
              {formatCurrency(safeNumber(data.payoutOperations.totalPayoutVolume))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wallet Reservations */}
      <Card className="admin-card-plain">
        <CardHeader className="admin-card-header">
          <CardTitle className="admin-card-title flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Wallet Reservations
          </CardTitle>
        </CardHeader>
        <CardContent className="admin-card-body">
          <div className="space-y-4">
            <div>
              <div className="admin-text-xs admin-text-muted">Total reserved</div>
              <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600 }}>
                {formatCurrency(safeNumber(data.walletReservations.totalReserved))}
              </div>
            </div>

            <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px' }}>
              <div>
                <div className="admin-text-xs admin-text-muted">Critical</div>
                <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600, color: 'var(--admin-danger)' }}>
                  {safeNumber(data.walletReservations.criticalReservations)}
                </div>
              </div>
              <div>
                <div className="admin-text-xs admin-text-muted">Expiring soon</div>
                <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600, color: 'var(--admin-warning)' }}>
                  {safeNumber(data.walletReservations.expiringSoon)}
                </div>
              </div>
            </div>

            {data.walletReservations.criticalReservations > 0 && (
              <div style={{ marginTop: '8px' }}>
                <Badge className="admin-badge-danger">
                  {safeNumber(data.walletReservations.criticalReservations)} critical reservations
                </Badge>
              </div>
            )}

            {data.walletReservations.expiringSoon > 0 && (
              <div style={{ marginTop: '8px' }}>
                <Badge className="admin-badge-warning">
                  {safeNumber(data.walletReservations.expiringSoon)} expiring soon
                </Badge>
              </div>
            )}

            {data.walletReservations.criticalReservations === 0 && data.walletReservations.expiringSoon === 0 && (
              <div style={{ marginTop: '8px' }}>
                <Badge className="admin-badge-success">
                  All reservations healthy
                </Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}